import { type EntityNotesResponse } from "@swivu/client-api";
import { PencilSquareIcon } from "@heroicons/react/20/solid/index.js";
import { NotesDesktop, NotesMobile, useMediaQuery } from "@swivu/shared-ui";
import CommonStyles from "../CommonStyles.js";
import { useSearchParams } from "react-router-dom";

type NotesProps = {
  notes: EntityNotesResponse;
  title: string;
};

const checkIfNoteIsEmpty = (note: EntityNotesResponse[number]) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(note.content, "text/html");
  const doc2 = parser.parseFromString(note?.description ?? "", "text/html");
  const text = (doc.body.textContent ?? "") + (doc2.body.textContent ?? "");
  return text.trim() !== "";
};

export const NotesList = ({ notes, title }: NotesProps) => {
  // update search params to remove notes key
  const [searchParams, setSearchParams] = useSearchParams();

  if (!notes || notes?.length === 0) return null;
  // filter out notes with empty content, including empty strings and <br />
  const filteredNotes = notes.filter(checkIfNoteIsEmpty);

  if (filteredNotes.length === 0) return null;
  return (
    <CommonStyles
      Icon={PencilSquareIcon}
      tone="notes"
      title={`Notes`}
      badge={notes.length}
      cardTitle={`${title}`}
      cardSubtitle={`Notes`}
      onOpenChange={(open) => {
        if (!open) {
          searchParams.delete("notes");
          setSearchParams(searchParams);
        }
      }}
    >
      <NotesMobileOrDesktop notes={filteredNotes} />
    </CommonStyles>
  );
};

const NotesMobileOrDesktop = ({ notes }: Omit<NotesProps, "title">) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const sortedNotes = [...notes]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .sort(({ order }, { order: order2 }) => order - order2);

  if (isDesktop) {
    return <NotesDesktop notes={sortedNotes} layoutId="entityNotes" />;
  }

  return <NotesMobile notes={sortedNotes} />;
};
